import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { PortalBalanceResponse } from "./contracts/PortalBalanceResponse";
import { PortalContractResponse } from "./contracts/PortalContractResponse";
import { PortalMandateResponse } from "./contracts/PortalMandateResponse";
import { PortalMe } from "./models/PortalMe";
import { PortalOccurrence } from "./models/PortalOccurrence";
import { PortalVisitResponse } from "./contracts/PortalVisitResponse";
import { clearPortalSession, PORTAL_SESSION_KEY, PORTAL_TOKEN_KEY, StoredPortalSession } from "./portalAxios";
import { AbsenceResponse } from "../absences/contracts/AbsenceResponse";
import { PortalAbsenceOverviewEntryResponse } from "./contracts/PortalAbsenceOverviewEntryResponse";
import { PortalMaterialResponse } from "./contracts/PortalMaterialResponse";
import { BookableOccurrenceResponse } from "./contracts/BookableOccurrenceResponse";
import { applyBookedSeat } from "./utils/applyBookedSeat";
import { Progress } from "../training/models/Progress";
import {
  acceptPortalContract,
  bookOccurrence,
  createPortalAbsence,
  deletePortalAbsence,
  deletePortalMaterial,
  fetchPortalAbsenceOverview,
  fetchBookableOccurrences,
  fetchPortalAbsences,
  fetchPortalBalance,
  fetchPortalMandate,
  fetchPortalMaterials,
  fetchPortalMe,
  fetchPortalOccurrences,
  fetchPortalPendingContracts,
  fetchPortalVisits,
  fetchPortalProgress,
  redeemPortalToken,
  requestPortalLink,
  updatePortalAbsence,
  uploadPortalMaterial,
  verifyPortalCode,
  toStoredPortalSession,
} from "./slice-extensions/portal-slice-extension";
import { updatePortalProfile } from "./slice-extensions/portal-me-slice-extension";

export type PortalSession = StoredPortalSession;

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface PortalState {
  session: PortalSession | null;
  requestLinkStatus: RequestStatus;
  redeemStatus: RequestStatus;
  verifyCodeStatus: RequestStatus;
  verifyCodeError: string | null;
  me: PortalMe | null;
  meStatus: RequestStatus;
  /** The member (self or ward) the pages show, mirrored from `?member=` by usePortalMember; null = self. */
  selectedMemberId: string | null;
  occurrencesByMemberId: Record<string, PortalOccurrence[]>;
  visitsByMemberId: Record<string, PortalVisitResponse[]>;
  progressByMemberId: Record<string, Progress[]>;
  balancesByMemberId: Record<string, PortalBalanceResponse>;
  mandatesByMemberId: Record<string, PortalMandateResponse>;
  pendingContractsByMemberId: Record<string, PortalContractResponse[]>;
  absencesByMemberId: Record<string, AbsenceResponse[]>;
  materialsByMemberId: Record<string, PortalMaterialResponse[]>;
  absenceOverview: PortalAbsenceOverviewEntryResponse[];
  bookableByMemberId: Record<string, BookableOccurrenceResponse[]>;
}

/** Rehydrates the portal session; the token itself lives in localStorage for the axios interceptor. */
const loadStoredSession = (): PortalSession | null => {
  if (!localStorage.getItem(PORTAL_TOKEN_KEY)) {
    return null;
  }

  try {
    const raw = localStorage.getItem(PORTAL_SESSION_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as PortalSession;
    // A session stored before US-69 carries no contexts.
    return { ...stored, contexts: stored.contexts ?? [] };
  } catch {
    return null;
  }
};

const initialState: PortalState = {
  session: loadStoredSession(),
  requestLinkStatus: "idle",
  redeemStatus: "idle",
  verifyCodeStatus: "idle",
  verifyCodeError: null,
  me: null,
  meStatus: "idle",
  selectedMemberId: null,
  occurrencesByMemberId: {},
  visitsByMemberId: {},
  progressByMemberId: {},
  balancesByMemberId: {},
  mandatesByMemberId: {},
  pendingContractsByMemberId: {},
  absencesByMemberId: {},
  materialsByMemberId: {},
  absenceOverview: [],
  bookableByMemberId: {},
};

/** A fresh sign-in may be another member: `me` and the pick are fetched and resolved again. */
const resetSignedInMember = (state: PortalState) => {
  state.me = null;
  state.meStatus = "idle";
  state.selectedMemberId = null;
};

const portalSlice = createSlice({
  name: "portal",
  initialState,
  reducers: {
    portalSignOut: (state) => {
      clearPortalSession();
      state.session = null;
      state.me = null;
      state.meStatus = "idle";
      state.selectedMemberId = null;
      state.occurrencesByMemberId = {};
      state.visitsByMemberId = {};
      state.progressByMemberId = {};
      state.balancesByMemberId = {};
      state.mandatesByMemberId = {};
      state.pendingContractsByMemberId = {};
      state.absencesByMemberId = {};
      state.materialsByMemberId = {};
      state.absenceOverview = [];
      state.bookableByMemberId = {};
      state.redeemStatus = "idle";
      state.requestLinkStatus = "idle";
      state.verifyCodeStatus = "idle";
      state.verifyCodeError = null;
    },
    /** A member-only sign-in on /login opens the portal without a reload (US-113): its session takes over. */
    portalSessionOpened: (state, action: PayloadAction<PortalSession>) => {
      resetSignedInMember(state);
      state.session = action.payload;
    },
    setPortalSelectedMember: (state, action: PayloadAction<string>) => {
      state.selectedMemberId = action.payload;
    },
    resetRequestLinkStatus: (state) => {
      state.requestLinkStatus = "idle";
      state.verifyCodeStatus = "idle";
      state.verifyCodeError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestPortalLink.pending, (state) => {
        state.requestLinkStatus = "loading";
      })
      .addCase(requestPortalLink.fulfilled, (state) => {
        state.requestLinkStatus = "succeeded";
      })
      .addCase(requestPortalLink.rejected, (state) => {
        state.requestLinkStatus = "failed";
      })
      .addCase(redeemPortalToken.pending, (state) => {
        state.redeemStatus = "loading";
      })
      .addCase(redeemPortalToken.fulfilled, (state, action) => {
        state.redeemStatus = "succeeded";
        resetSignedInMember(state);
        state.session = toStoredPortalSession(action.payload);
      })
      .addCase(redeemPortalToken.rejected, (state) => {
        state.redeemStatus = "failed";
      })
      .addCase(verifyPortalCode.pending, (state) => {
        state.verifyCodeStatus = "loading";
        state.verifyCodeError = null;
      })
      .addCase(verifyPortalCode.fulfilled, (state, action) => {
        state.verifyCodeStatus = "succeeded";
        resetSignedInMember(state);
        state.session = toStoredPortalSession(action.payload);
      })
      .addCase(verifyPortalCode.rejected, (state, action) => {
        state.verifyCodeStatus = "failed";
        state.verifyCodeError = action.payload?.message ?? null;
      })
      .addCase(fetchPortalMe.pending, (state) => {
        state.meStatus = "loading";
      })
      .addCase(fetchPortalMe.fulfilled, (state, action: PayloadAction<PortalMe>) => {
        state.meStatus = "succeeded";
        state.me = action.payload;
      })
      .addCase(fetchPortalMe.rejected, (state) => {
        state.meStatus = "failed";
      })
      .addCase(updatePortalProfile.fulfilled, (state, action) => {
        // The saved language is the member's own from now on (self or ward).
        const { memberId, languageCode } = action.payload;
        const members = state.me ? [state.me.self, ...state.me.wards] : [];
        const member = members.find((m) => m.memberId === memberId);
        if (member) member.languageCode = languageCode;
      })
      .addCase(fetchPortalOccurrences.fulfilled, (state, action) => {
        state.occurrencesByMemberId[action.payload.memberId] = action.payload.occurrences;
      })
      .addCase(fetchPortalVisits.fulfilled, (state, action) => {
        state.visitsByMemberId[action.payload.memberId] = action.payload.visits;
      })
      .addCase(fetchPortalProgress.fulfilled, (state, action) => {
        state.progressByMemberId[action.payload.memberId] = action.payload.progress;
      })
      .addCase(fetchPortalBalance.fulfilled, (state, action) => {
        state.balancesByMemberId[action.payload.memberId] = action.payload;
      })
      .addCase(fetchPortalMandate.fulfilled, (state, action) => {
        state.mandatesByMemberId[action.payload.memberId] = action.payload.mandate;
      })
      .addCase(fetchPortalPendingContracts.fulfilled, (state, action) => {
        state.pendingContractsByMemberId[action.payload.memberId] = action.payload.contracts;
      })
      .addCase(acceptPortalContract.fulfilled, (state, action) => {
        const { memberId, contract } = action.payload;
        state.pendingContractsByMemberId[memberId] =
          (state.pendingContractsByMemberId[memberId] ?? []).filter((c) => c.id !== contract.id);
        const balance = state.balancesByMemberId[memberId];
        if (balance) {
          balance.contracts = balance.contracts.map((c) => (c.id === contract.id ? contract : c));
        }
      })
      .addCase(fetchPortalAbsences.fulfilled, (state, action) => {
        state.absencesByMemberId[action.payload.memberId] = action.payload.absences;
      })
      .addCase(createPortalAbsence.fulfilled, (state, action) => {
        const { memberId, absence } = action.payload;
        const list = state.absencesByMemberId[memberId] ?? [];
        state.absencesByMemberId[memberId] = [...list, absence].sort((a, b) =>
          a.startDate.localeCompare(b.startDate),
        );
      })
      .addCase(updatePortalAbsence.fulfilled, (state, action) => {
        const { memberId, absence } = action.payload;
        state.absencesByMemberId[memberId] = (state.absencesByMemberId[memberId] ?? []).map(
          (item) => (item.id === absence.id ? absence : item),
        );
      })
      .addCase(deletePortalAbsence.fulfilled, (state, action) => {
        const { memberId, absenceId } = action.payload;
        state.absencesByMemberId[memberId] = (state.absencesByMemberId[memberId] ?? []).filter(
          (item) => item.id !== absenceId,
        );
      })
      .addCase(fetchBookableOccurrences.fulfilled, (state, action) => {
        state.bookableByMemberId[action.payload.memberId] = action.payload.occurrences;
      })
      .addCase(bookOccurrence.fulfilled, (state, action) => {
        const { memberId, seat } = action.payload;
        state.bookableByMemberId[memberId] = applyBookedSeat(
          state.bookableByMemberId[memberId] ?? [],
          seat,
        );
      })
      .addCase(fetchPortalAbsenceOverview.fulfilled, (state, action) => {
        state.absenceOverview = action.payload;
      })
      .addCase(fetchPortalMaterials.fulfilled, (state, action) => {
        state.materialsByMemberId[action.payload.memberId] = action.payload.materials;
      })
      .addCase(uploadPortalMaterial.fulfilled, (state, action) => {
        const { memberId, material } = action.payload;
        state.materialsByMemberId[memberId] = [
          material,
          ...(state.materialsByMemberId[memberId] ?? []),
        ];
      })
      .addCase(deletePortalMaterial.fulfilled, (state, action) => {
        const { memberId, materialId } = action.payload;
        state.materialsByMemberId[memberId] = (state.materialsByMemberId[memberId] ?? []).filter(
          (item) => item.id !== materialId,
        );
      });
  },
});

export const { portalSessionOpened, portalSignOut, resetRequestLinkStatus, setPortalSelectedMember } =
  portalSlice.actions;

export const selectPortalSession = (state: RootState) => state.portal.session;
export const selectPortalRequestLinkStatus = (state: RootState) => state.portal.requestLinkStatus;
export const selectPortalRedeemStatus = (state: RootState) => state.portal.redeemStatus;
export const selectPortalVerifyCodeStatus = (state: RootState) => state.portal.verifyCodeStatus;
export const selectPortalVerifyCodeError = (state: RootState) => state.portal.verifyCodeError;
export const selectPortalMe = (state: RootState) => state.portal.me;
export const selectPortalMeStatus = (state: RootState) => state.portal.meStatus;
/** The member the portal pages show: the switcher's pick, else the signed-in member. */
export const selectPortalTargetMemberId = (state: RootState): string | null =>
  state.portal.selectedMemberId ?? state.portal.me?.self.memberId ?? null;
export const selectPortalOccurrences = (memberId: string) => (state: RootState) =>
  state.portal.occurrencesByMemberId[memberId];
export const selectPortalVisits = (memberId: string) => (state: RootState) =>
  state.portal.visitsByMemberId[memberId];
export const selectPortalProgress = (memberId: string) => (state: RootState): Progress[] | undefined =>
  state.portal.progressByMemberId[memberId];
export const selectPortalBalance = (memberId: string) => (state: RootState) =>
  state.portal.balancesByMemberId[memberId];
export const selectPortalMandate = (memberId: string) => (state: RootState) =>
  state.portal.mandatesByMemberId[memberId];
export const selectPortalPendingContracts = (memberId: string) => (state: RootState) =>
  state.portal.pendingContractsByMemberId[memberId];
export const selectPortalAbsences = (memberId: string) => (state: RootState) =>
  state.portal.absencesByMemberId[memberId];
export const selectPortalMaterials = (memberId: string) => (state: RootState) =>
  state.portal.materialsByMemberId[memberId];
export const selectPortalAbsenceOverview = (state: RootState) => state.portal.absenceOverview;
export const selectPortalBookableOccurrences = (memberId: string) => (state: RootState) =>
  state.portal.bookableByMemberId[memberId];

export default portalSlice.reducer;
